import { create, tsx } from '@dojo/framework/core/vdom';
import { Link } from '@dojo/framework/routing/Link';
import { ConnectStrip } from '../../widgets/ConnectStrip';
import { FAQ } from '../../widgets/FAQ';
import { Hero } from '../../widgets/Hero';
import SummaryList from '../../widgets/SummaryList';
import Summary from '../../widgets/Summary';
import Image from '../../widgets/Image';

import Head from '../../widgets/Head';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import WhySitePen from '../../widgets/WhySitePen';

const backEndServicesImg = require('../../assets/images/icon-illustration/back-end-services.svg');
const backEndToolsImg = require('../../assets/images/icon-illustration/back-end-tools.svg');
const cloudImg = require('../../assets/images/icon-illustration/cloud-application.svg');
const databaseTechnologyImg = require('../../assets/images/icon-illustration/database-technology.svg');
const frontEndToolsImg = require('../../assets/images/icon-illustration/front-end-tools.svg');
const performanceImg = require('../../assets/images/illustration/high-performance.svg');

const factory = create();

export const DevOpsSolutions = factory(function DevOpsSolutions() {
	return (
		<div>
			<Head
				title="Cloud and DevOps Services & Solutions | SitePen"
				description="SitePen Cloud and DevOps solutions improve the quality and reliability of software infrastructure and tooling with automated processes."
			/>

			<Hero background="level3Blue">
				{{
					xlTitle: (
						<div>
							Cloud and DevOps
							<br />
							Engineering
						</div>
					)
				}}
			</Hero>

			<Section type="leading">
				<ContentWrapper>
					<Summary reverse collapse="large">
						{{
							title: 'Maximize Quality and Efficiency',
							blurb:
								'Delivering robust software requires rigorous ongoing testing in an environment that closely mirrors a production system environment. The ability to execute such testing should be available to all delivery team members whenever needed. Providing such as testing and infrastructure capability can quickly become a full-time job; therefore, automation is critical in efficiently and cost-effectively solving the problem. Successful software products require a software delivery lifecycle (SDLC) that is highly automated and allows delivery teams to focus on implementing and verifying business value rather than battling infrastructure and other unrelated issues.',
							image: <Image src={performanceImg} size="lg" />
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ContentWrapper>
					<WhySitePen reverse>
						{{
							blurb:
								'At SitePen, our focus is on the end-to-end delivery of high-quality software solutions that set your technology organization up for ongoing success. To maximize our efficiency in achieving this goal, we embrace devops automation practices in creating a robust SDLC that allows continuous delivery of business value to end-users. We view testing as an essential, non-negotiable part of the software lifecycle. Establishing a reliable and automated SDLC means our solutions are continuously tested and only deployed to customers when delivery pipelines report sufficient test success rates and other solution quality metrics. Our goal is to deliver high-quality software to end-users iteratively and consistently.'
						}}
					</WhySitePen>
				</ContentWrapper>
			</Section>
			<Section>
				<ContentWrapper>
					<SummaryList>
						{{
							title: 'DevOps Consulting Services',
							blurb: (
								<virtual>
									At SitePen, we typically include devops automation as part of a
									more comprehensive solution delivery engagement; however, we can
									also help in the analysis and uplift of devops practices within
									your existing products.
								</virtual>
							),
							items: [
								{
									title: 'Enterprise Cloud Services',
									image: <Image src={cloudImg} align="center" />,
									blurb: (
										<virtual>
											We work with business, technology, and operations
											stakeholders in your organization to help design and
											implement{' '}
											<Link to="servicesEnterpriseCloudServices">
												cloud-first strategies and solutions
											</Link>
											.
										</virtual>
									)
								},
								{
									title: 'Infrastructure Management',
									image: <Image src={frontEndToolsImg} align="center" />,
									blurb: (
										<virtual>
											Managing the complexity of modern internet-scale
											solutions requires automation and robust change
											management procedures for all aspects of the system,
											including infrastructure. At SitePen, we favor
											implementing infrastructure-as-code (IaC) practices to
											define infrastructure in a format that lends itself to
											automation and allows version control management similar
											to software. Our IaC expertise covers a range of public
											cloud infrastructure definition conventions and more
											generalized tools such as Ansible.
										</virtual>
									)
								},
								{
									title: 'Code Inspection & Integration',
									image: <Image src={backEndServicesImg} align="center" />,
									blurb: (
										<virtual>
											Solution quality is essential to us at SitePen, and we
											strive to implement continuous delivery pipelines that
											allow for rigorous ongoing testing and code quality
											monitoring. Metrics generated from these pipelines
											enable us to continuously improve our solution
											implementation quality while simultaneously delivering
											product functionality. Automation is key to achieving
											such a continuous integration system, which allows for
											greater focus on the solution’s functionality and
											quality rather than ancillary concerns.
										</virtual>
									)
								},
								{
									title: 'Configuration Management',
									image: <Image src={backEndToolsImg} align="center" />,
									blurb: (
										<virtual>
											Robust, versioned, and automated configuration
											management is also required when creating a
											fully-automated continuous delivery pipeline - we view
											this as a natural extension of infrastructure-as-code.
											Everything related to a software system’s code, its
											environment definitions, and the configuration needed
											for it to run, should all be documented and version
											controlled. SitePen’s expertise covers a range of public
											cloud configuration management systems and open-source
											options, either as part of broader IaC definitions or
											used as a standalone config management build-time,
											deployment, or runtime component.
										</virtual>
									)
								},
								{
									title: 'DevOps Automation Service',
									image: <Image src={databaseTechnologyImg} align="center" />,
									blurb: (
										<virtual>
											SitePen’s experience in delivering software solutions
											over many years has given us a greater perspective of
											where the software industry has come from and why the
											goal for more automation is critical to modern software
											projects’ success. We can help your organization analyze
											existing delivery life cycles to identify opportunities
											for more automated pipelines and help implement
											continuous integration improvements within your projects
											to assist in ongoing solution delivery undertaken by
											your technology teams.
										</virtual>
									)
								}
							]
						}}
					</SummaryList>
				</ContentWrapper>
			</Section>
			<Section shaded>
				<ConnectStrip />
			</Section>
			<Section trailing>
				<FAQ title="DevOps FAQs">
					{[
						{
							title: 'Will DevOps Help Me?',
							content: (
								<p>
									If your business relies on the implementation or hosting of
									software systems, yes, devops will help. While there may be a
									higher upfront cost to implement an automated delivery pipeline
									versus manually building, configuring, and deploying a new
									version of your software system, the benefits gained over the
									application’s full lifetime will significantly outweigh the
									initial automation cost. The cost benefits of implementing
									devops automation only ever increase each time a software
									solution needs to be built, tested, and deployed to end-users.
								</p>
							)
						},
						{
							title: 'What DevOps Tools Does SitePen Use?',
							content: (
								<p>
									At SitePen, we favor portable, open-source solutions wherever
									possible. However, we also have expertise in the continuous
									delivery tools and capabilities provided by major cloud vendors.
									Simplicity is vital in our approach - if a vendor-specific
									solution does the job and keeps things clear and simple, the
									need for additional abstraction and layering of devops tools is
									likely unnecessary. Our view is to find the right tool for the
									job, with some upfront investment in automation paying immediate
									and continuous dividends throughout a software system’s
									implementation and maintenance.
								</p>
							)
						},
						{
							title: 'How Long Is the DevOps Process?',
							content: (
								<p>
									While the initial setup of a fully automated delivery pipeline
									may take some time, it is often a highly standardized process
									between projects. The process’s commodity nature means there are
									many off-the-shelf solutions available today that handle every
									aspect you need. The investment for setting up devops automation
									for a single project is a one-off cost. Applying those same
									principles and pipelines to future projects becomes more of a
									cookie-cutter process requiring far less investment. The same is
									true whether using commodity devops solutions or implementing
									in-house continuous delivery pipelines.
								</p>
							)
						}
					]}
				</FAQ>
			</Section>
		</div>
	);
});

export default DevOpsSolutions;
